import { useCallback, useEffect, useState } from 'react'
import { viewTheme } from './theme'

/** The ONE ResizeObserver primitive (shared-primitives guard). Returns a
 *  CALLBACK ref: the observer attaches when the element appears, so a host
 *  that mounts its measured node after a load gate (the dashboard canvas)
 *  is measured too — a plain useRef read once on mount would see null there
 *  and never observe. Callers keep writing `ref={ref}`. */
export function useMeasuredCanvas() {
  const [el, setEl] = useState<HTMLDivElement | null>(null)
  const ref = useCallback((node: HTMLDivElement | null) => setEl(node), [])
  const [size, setSize] = useState<{ W: number; H: number; measured: boolean }>({ W: viewTheme.canvas.width, H: viewTheme.canvas.height, measured: false })
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !el) return
    // CONSUMPTION CONTRACT (cold-read H1): consumers derive their drawn
    // HEIGHT from W only in auto-height hosts — deriving it from the
    // measured H loops there (the gallery cards grew unboundedly). H is
    // safe only where the host's height is definite (a grid tile, a flex
    // slot with basis 0). Width alone is enough to count as measured.
    const measure = (width: number, height: number) => {
      if (width <= 0) return
      const W = Math.round(width), H = Math.round(height)
      // Equality guard: a ResizeObserver tick at an identical size must not
      // re-render every consumer (a scrollbar toggle can tick repeatedly).
      setSize((prev) => (prev.measured && prev.W === W && prev.H === H ? prev : { W, H, measured: true }))
    }
    // First measurement synchronously (jsdom fakes and late mounts alike),
    // then every resize.
    measure(el.clientWidth, el.clientHeight)
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect
      if (r) measure(r.width, r.height)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [el])
  /** `measured`: false until the element has reported a real size — callers
   *  with a full-size default (the dashboard cell) keep it until then. */
  return { ref, W: size.W, H: size.H, measured: size.measured }
}
